/**
 * Decision 0086 (build map `docs/design/workspace-pages/build-map.md` §1.2, §1.5, §2, §3.2, §8.1): workspace pages.
 *
 * A first-party workspace document store in `WorkspaceDO` (not a hidden project): `people/<handle>.md` (one per member,
 * bound to the user id, frontmatter maintained from the account), `guidelines/<topic>.md` (owner and admins write) and
 * (decision 0100) `runners/capabilities.md` (what works where; leads, owner and admins write it).
 * In a project they are addressed as `workspace/<path>`; project documents may not use that prefix.
 *
 * Paths are the contract. This module names them, classifies a path, holds the caps and budgets for context pinning,
 * the person template and the wire shapes of the Worker routes and the WorkspaceDO RPC.
 */
import { Schema } from "effect"
import { Actor } from "./common"
import { docPathKey } from "./doc-path"
import { DocPath } from "./documents"
import { MemberRole } from "./domain/workspace"
import { ArtifactId, Handle, Millis, NonNegInt, PosInt, ProjectId, ProposalId, Sha256, UserId } from "./ids"

// #region paths
/** The project-side address of a workspace page: `workspace/<workspace path>` (W4). Reserved in every project. */
export const WORKSPACE_PREFIX = "workspace/"
/** `people/<handle>.md`: one page per member (W2). */
export const PEOPLE_DIR = "people/"
/** `guidelines/<topic>.md`: how the whole workspace works (W2). */
export const WORKSPACE_GUIDELINES_DIR = "guidelines/"
/**
 * Decision 0100: `runners/capabilities.md`, the one page of what works where (which runner, profile, CLI and account
 * could or could not do what, with run and item refs as evidence). Leads write it directly; free Markdown.
 */
export const CAPABILITIES_PAGE_PATH = "runners/capabilities.md"

/** What a workspace page is. Other paths are refused. */
export const WorkspacePageKind = Schema.Literals(["person", "workspace_guideline", "capabilities"])
export type WorkspacePageKind = typeof WorkspacePageKind.Type

const PERSON_PATH = /^people\/([a-z0-9][a-z0-9_-]{0,31})\.md$/
/** A guideline topic slug (§4.1): `[a-z0-9-]{1,60}`. */
export const WORKSPACE_TOPIC = /^[a-z0-9-]{1,60}$/
const GUIDELINE_PATH = /^guidelines\/([a-z0-9-]{1,60})\.md$/

/** The refusal for a write anywhere else (§1.2). */
export const WORKSPACE_PATH_REFUSAL =
  "workspace pages are people/<handle>.md, guidelines/<topic>.md and runners/capabilities.md"

/** The kind of a workspace-relative path (`people/jannik.md`, `guidelines/stack.md`), or null. Case-insensitive. */
export const workspacePageKindOf = (path: string): WorkspacePageKind | null => {
  const key = docPathKey(path)
  if (PERSON_PATH.test(key)) return "person"
  if (GUIDELINE_PATH.test(key)) return "workspace_guideline"
  if (key === CAPABILITIES_PAGE_PATH) return "capabilities"
  return null
}

/** The handle of a `people/<handle>.md` path (as written, lowercased), or null. */
export const personHandleOf = (path: string): string | null => PERSON_PATH.exec(docPathKey(path))?.[1] ?? null

/** The topic of a `guidelines/<topic>.md` path (lowercased), or null. */
export const workspaceTopicOf = (path: string): string | null =>
  GUIDELINE_PATH.exec(docPathKey(path))?.[1] ?? null

/** `people/<handle>.md`. */
export const personPagePath = (handle: string): string => `${PEOPLE_DIR}${handle}.md`

/** `guidelines/<topic>.md` (workspace-relative). */
export const workspaceGuidelinePath = (topic: string): string => `${WORKSPACE_GUIDELINES_DIR}${topic}.md`

/** Is `path` a project-side workspace address (`workspace/…`)? Case-insensitive. */
export const isWorkspaceProjectPath = (path: string): boolean => docPathKey(path).startsWith(WORKSPACE_PREFIX)

/** Workspace path → project-side address (`people/x.md` → `workspace/people/x.md`); null when not a workspace page. */
export const toProjectPath = (path: string): string | null =>
  workspacePageKindOf(path) === null ? null : `${WORKSPACE_PREFIX}${path}`

/** Project-side address → workspace path (`workspace/people/x.md` → `people/x.md`); null for any other path. */
export const fromProjectPath = (path: string): string | null => {
  if (!isWorkspaceProjectPath(path)) return null
  const rest = path.slice(WORKSPACE_PREFIX.length)
  return workspacePageKindOf(rest) === null ? null : rest
}
// #endregion paths

// #region caps
/** Per-page and per-set byte caps (UTF-8) for pinned workspace pages (§3.2). */
export const WORKSPACE_CAPS = {
  person: 2_048,
  peopleTotal: 4_096,
  guideline: 3_072,
  guidelinesTurn: 6_144,
  guidelinesWorker: 4_096,
  /** Decision 0147: a worker's or validator's people pages (the asker, the accountable person), smaller than a turn's. */
  personWorker: 1_024,
  peopleWorker: 2_048,
  /** Decision 0100: the capabilities page, a hard cap on every write (summarise older rows). */
  capabilities: 6_144,
} as const

/** The `## Workspace pages` section's own budget, separate from `PAGE_BUDGET` and the linked-document budget (W5). */
export const WORKSPACE_BUDGET = { turn: 10_240, worker: 6_144 } as const

/** Text limit per version (§1.3): Markdown only. */
export const WORKSPACE_PAGE_MAX_BYTES = 65_536
/** At most this many live guidelines per workspace (§1.3). */
export const WORKSPACE_GUIDELINES_MAX = 200

/**
 * The `LoadedDocument.reason` a pinned workspace page is reported with (§3.1): no new reasons, the `workspace/` path
 * marks them. People pages load as `project_page`, workspace guidelines as `guideline`.
 */
export const WORKSPACE_LOADED_REASON = {
  person: "project_page",
  workspace_guideline: "guideline",
  capabilities: "project_page",
} as const satisfies Record<WorkspacePageKind, "project_page" | "guideline">
// #endregion caps

// #region capabilities
/** Decision 0100: the refusal of a capabilities page over its cap. */
export const CAPABILITIES_CAP_REFUSAL = `runners/capabilities.md holds at most ${WORKSPACE_CAPS.capabilities} bytes: summarise older rows, drop entries that are stale (older than 14 days or than the runner's last version or profile change; the history keeps them) and post again`

/** Decision 0100: the capabilities page Minga seeds (as `system{pages}`); leads keep it from there. */
export const CAPABILITIES_TEMPLATE = [
  "# What works where",
  "",
  "Observed by agents from runs. What runners *declare* (tools, repos, profiles, quota) is on the Runners page and in",
  "`list_runners`; this page records what actually worked or failed. One section per place (runner · profile: CLI ·",
  "account · access mode), newest first. Status: `works`, `fails: <why>` or `needed`. Evidence: run and item refs.",
  "An entry older than 14 days, or than the runner's last version or profile change, counts as unknown: try again.",
  "",
  "## <runner> · <profile> (<CLI> · <account> · <access mode>)",
  "| Capability | Status | Evidence | Seen |",
  "| --- | --- | --- | --- |",
  "",
  "## Needed, not available anywhere",
  "",
].join("\n")
// #endregion capabilities

// #region people
/** The comment line closing the account-maintained frontmatter (§1.5). */
export const PERSON_FRONTMATTER_NOTE = "# maintained by Minga from the account; edits here are replaced"

/**
 * The `##` sections of a new person page's body (§1.5). Decision 0127: `Decides` lists the topics the person decides
 * (e.g. "product, copy, social" or "technical, infrastructure, spending"); agents set a card's `to` from it.
 */
export const PERSON_SECTIONS = [
  "About",
  "Decides",
  "How I work",
  "Defaults and preferences",
  "Don't",
] as const

/** A new person page's body (the frontmatter is prepended by core `composePersonPage`). */
export const PERSON_TEMPLATE = (name: string): string =>
  [`# ${name}`, "", ...PERSON_SECTIONS.flatMap((s) => [`## ${s}`, ""])].join("\n")

/** A person page's frontmatter, derived from the account by `system{people}`; never typed by hand. No email (§1.5). */
export const PersonFrontmatter = Schema.Struct({
  userId: UserId,
  handle: Handle,
  name: Schema.String,
  /** `github_identity.login`, only while GitHub is linked. */
  github: Schema.optionalKey(Schema.String),
  /** The workspace role. */
  role: MemberRole,
  /** `YYYY-MM-DD`. */
  memberSince: Schema.String,
})
export type PersonFrontmatter = typeof PersonFrontmatter.Type
// #endregion people

// #region store
/** A workspace document chain id: `wkd_<ulid>`. */
export const WorkspaceDocId = Schema.String.check(Schema.isPattern(/^wkd_[0-9a-hjkmnp-tv-z]{26}$/)).pipe(
  Schema.brand("WorkspaceDocId"),
)
export type WorkspaceDocId = typeof WorkspaceDocId.Type

/** The head (current version) of one live workspace page chain. `path` is workspace-relative. */
export const WorkspacePageHead = Schema.Struct({
  docId: WorkspaceDocId,
  /** The version's id, an `ArtifactId` so `LoadedDocument` takes it unchanged. */
  versionId: ArtifactId,
  path: DocPath,
  kind: WorkspacePageKind,
  title: Schema.String,
  n: PosInt,
  sha256: Sha256,
  sizeBytes: NonNegInt,
  /** A person, or `system{people}`. */
  by: Actor,
  updatedAt: Millis,
  /** Person pages: the account. Null for guidelines. */
  userId: Schema.NullOr(UserId),
})
export type WorkspacePageHead = typeof WorkspacePageHead.Type

/** Where an adopted version came from (§4.2): the project card that accepted an agent's draft. */
export const AdoptSource = Schema.Struct({
  projectId: ProjectId,
  proposalId: ProposalId,
  draftId: ArtifactId,
})
export type AdoptSource = typeof AdoptSource.Type

/** One entry of a chain's version list. */
export const WorkspacePageVersionEntry = Schema.Struct({
  n: PosInt,
  by: Actor,
  at: Millis,
  /** The one-line why ("Accepted P-12 from Dictly", "Account details updated"). */
  note: Schema.NullOr(Schema.String),
})
export type WorkspacePageVersionEntry = typeof WorkspacePageVersionEntry.Type

/** One version of a page: its head-shaped meta, the text (hash-checked), the people frontmatter split out, history. */
export const WorkspacePageVersion = Schema.Struct({
  ...WorkspacePageHead.fields,
  /** The text of this version (people pages: frontmatter included). */
  text: Schema.String,
  /** People pages: the parsed frontmatter; null for guidelines (or an unparsable block). */
  frontmatter: Schema.NullOr(PersonFrontmatter),
  /** People pages: the body without the frontmatter block; guidelines: the text. */
  body: Schema.String,
  note: Schema.NullOr(Schema.String),
  source: Schema.NullOr(AdoptSource),
  /** The chain is archived (a member left, a guideline removed). */
  archived: Schema.Boolean,
  /** This version is the chain's head (false for an older `?v=n`). */
  isHead: Schema.Boolean,
  versions: Schema.Array(WorkspacePageVersionEntry),
})
export type WorkspacePageVersion = typeof WorkspacePageVersion.Type
// #endregion store

// #region wire (Worker routes, §2.1)
/** `GET /api/workspaces/:ws/knowledge` row. */
export const WorkspaceKnowledgeEntry = Schema.Struct({
  path: DocPath,
  kind: WorkspacePageKind,
  title: Schema.String,
  n: PosInt,
  updatedAt: Millis,
  by: Actor,
  userId: Schema.NullOr(UserId),
  archived: Schema.Boolean,
})
export type WorkspaceKnowledgeEntry = typeof WorkspaceKnowledgeEntry.Type

/** `GET /api/workspaces/:ws/knowledge`. */
export const WorkspaceKnowledgeList = Schema.Struct({
  pages: Schema.Array(WorkspaceKnowledgeEntry),
  canWriteGuidelines: Schema.Boolean,
  /** The caller's own `people/<handle>.md`, null before the first sync. */
  myPersonPath: Schema.NullOr(DocPath),
})
export type WorkspaceKnowledgeList = typeof WorkspaceKnowledgeList.Type

/** `GET /api/workspaces/:ws/knowledge/search?q=` hit (top 20, live heads only). */
export const WorkspaceKnowledgeSearchHit = Schema.Struct({
  path: DocPath,
  title: Schema.String,
  snippet: Schema.String,
  n: PosInt,
})
export type WorkspaceKnowledgeSearchHit = typeof WorkspaceKnowledgeSearchHit.Type

/** `PUT /api/workspaces/:ws/knowledge/page`: a person's version, or a new guideline (`baseVersion: null`). */
export const WritePageBody = Schema.Struct({
  path: DocPath,
  text: Schema.String,
  /** The `n` the editor saw; null creates. A mismatch answers 409 with the current head. */
  baseVersion: Schema.NullOr(PosInt),
  note: Schema.optionalKey(Schema.NullOr(Schema.String)),
})
export type WritePageBody = typeof WritePageBody.Type

/** `POST /api/workspaces/:ws/knowledge/page/archive` (owner/admin, guidelines). */
export const ArchivePageBody = Schema.Struct({ path: DocPath, archived: Schema.Boolean })
export type ArchivePageBody = typeof ArchivePageBody.Type

/** A refused or conflicting write: 403 (§1.4), 404, 409 (stale base; `head` is the current one), 422 (path, size). */
export const WritePageError = Schema.Struct({
  error: Schema.String,
  head: Schema.optionalKey(WorkspacePageHead),
})
export type WritePageError = typeof WritePageError.Type
// #endregion wire

// #region rpc (WorkspaceDO, §2.2)
/** `knowledgeHeads(sinceRev)`: `heads` omitted when `rev` equals `sinceRev`. */
export interface KnowledgeHeadsResult {
  readonly rev: number
  readonly heads?: ReadonlyArray<WorkspacePageHead>
}

/** `knowledgePage(q)`: by workspace path or by user id (people), optionally an older version `n`. */
export interface KnowledgePageQuery {
  readonly path?: string
  readonly userId?: UserId
  readonly n?: number
}

/** `writeKnowledgePage(w)`. The Worker stores the bytes first (R2 `files/<sha256>`) and passes the text along. */
export interface WriteKnowledgePageInput {
  readonly actor: Actor
  /** The writer's workspace role from D1 (`highestRole`); null for `system{people}`. */
  readonly role: typeof MemberRole.Type | null
  readonly path: string
  readonly sha256: string
  readonly sizeBytes: number
  readonly text: string
  readonly baseVersion: number | null
  readonly note: string | null
  readonly source: AdoptSource | null
  /** Deduped in `wk_log`: a retried outbox adoption replays (`adopt:<projectId>:<proposalId>`). */
  readonly idempotencyKey: string
}

export type WriteKnowledgePageResult =
  | { readonly ok: true; readonly version: WorkspacePageHead }
  | {
      readonly ok: false
      readonly status: 403 | 404 | 409 | 422
      readonly error: string
      readonly head?: WorkspacePageHead
    }

/** `archiveKnowledgePage(a)`: owner/admin for guidelines; `system{people}` for person pages on leave and rejoin. */
export interface ArchiveKnowledgePageInput {
  readonly actor: Actor
  readonly role: typeof MemberRole.Type | null
  readonly path: string
  readonly archived: boolean
}

/** `syncPeople(members, identities)`: one member as the Worker read it from D1 (`workspaceMembers`, `MemberRow`). */
export interface PeopleSyncMember {
  readonly userId: string
  /** Better Auth `member.role` (comma-separated roles; `highestRole` picks one). */
  readonly role: string
  readonly name: string
  readonly email: string
  readonly image: string | null
  /** `member.createdAt` (ISO); `memberSince` is its date. */
  readonly createdAt: string
}

/** `syncPeople(members, identities)`: a linked GitHub login (`github_identity`). */
export interface PeopleSyncIdentity {
  readonly userId: string
  readonly login: string
}
// #endregion rpc
